import React, { useState, useEffect } from "react";
import classes from "./rabbitNameGenerator.module.css";

// Import the names data from the JSON file
import namesData from "./catNames.json";
import themesData from "./catNameThemes.json"


const RabbitNameGenerator = () => {
  const [gender, setGender] = useState("");
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [activeTab, setActiveTab] = useState("styles"); // 'styles' or 'themes'
  const [showModal, setShowModal] = useState(false);
  const [generatedName, setGeneratedName] = useState("");
  const [animating, setAnimating] = useState(false);

  const handleGenderSelect = (selectedGender) => {
    setGender(selectedGender);
  };

  const handleStyleToggle = (style) => {
    if (selectedStyles.includes(style)) {
      setSelectedStyles(selectedStyles.filter((item) => item !== style));
    } else {
      setSelectedStyles([...selectedStyles, style]);
    }
  };

  const handleThemeToggle = (theme) => {
    if (selectedThemes.includes(theme)) {
      setSelectedThemes(selectedThemes.filter((item) => item !== theme));
    } else {
      setSelectedThemes([...selectedThemes, theme]);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Function to generate a random name based on selected criteria
  const generateName = () => {
    // Validate selections
    if (
      !gender ||
      (selectedStyles.length === 0 && selectedThemes.length === 0)
    ) {
      alert("Please select a gender and at least one style or theme!");
      return;
    }

    // Start animation
    setAnimating(true);

    // Generate name after a short delay to show animation
    setTimeout(() => {
      let availableNames = [];
      const genderKey = gender === "boy" ? "Male" : "Female";

      // Collect names from selected styles
      selectedStyles.forEach((style) => {
        const styleCapitalized = style.charAt(0).toUpperCase() + style.slice(1);
        if (
          namesData[styleCapitalized] &&
          namesData[styleCapitalized][genderKey]
        ) {
          availableNames = [
            ...availableNames,
            ...namesData[styleCapitalized][genderKey],
          ];
        }
      });
      // selectedThemes.forEach(theme => { ... });
      selectedThemes.forEach((style) => {
        const styleCapitalized = style.charAt(0).toUpperCase() + style.slice(1);
        if (
          themesData[styleCapitalized] &&
          themesData[styleCapitalized][genderKey]
        ) {
          availableNames = [
            ...availableNames,
            ...themesData[styleCapitalized][genderKey],
          ];
        }
      });
      // If no names are available, use general names
      if (
        availableNames.length === 0 &&
        namesData["General"] &&
        namesData["General"][genderKey]
      ) {
        availableNames = namesData["General"][genderKey];
      }

      // Select a random name
      if (availableNames.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableNames.length);
        setGeneratedName(availableNames[randomIndex]);
        setShowModal(true);
      } else {
        alert(
          "No names found with your criteria. Please try different options."
        );
      }

      setAnimating(false);
    }, 1200);
  };

  return (
    <div className={classes.container}>
      <div className={classes.introText}>
        <h1>
          Hello and welcome to My Cat's Name, a fun and easy-to-use resource
          to help you find the perfect cat name!
        </h1>
        <p>
          Using our cat name generator is simple. First, pick the gender and
          style of names that you'd like. Do you prefer cute cat names,
          elegant names that sound refined, or classic cat names like Thumper
          and Fluffy? Next, click the button and we'll generate a list of
          awesome options.
        </p>
      </div>

      <div className={classes.generatorSection}>
        <h2 className={classes.generatorTitle}>Cat Name Generator</h2>
        <div className={classes.separator}></div>

        <div className={classes.genderSelection}>
          <div
            className={`${classes.genderOption} ${
              gender === "boy" ? classes.selected : ""
            }`}
            onClick={() => handleGenderSelect("boy")}
          >
            <div className={classes.genderCircle}>
              <div className={classes.innerCircle}></div>
            </div>
            <span>Boy</span>
          </div>

          <div
            className={`${classes.genderOption} ${
              gender === "girl" ? classes.selected : ""
            }`}
            onClick={() => handleGenderSelect("girl")}
          >
            <div className={classes.genderCircle}>
              <div className={classes.innerCircle}></div>
            </div>
            <span>Girl</span>
          </div>
        </div>

        <div className={classes.tabContainer}>
          <div
            className={`${classes.tab} ${
              activeTab === "styles" ? classes.activeTab : ""
            }`}
            onClick={() => handleTabClick("styles")}
          >
            Name Styles
          </div>
          <div
            className={`${classes.tab} ${
              activeTab === "themes" ? classes.activeTab : ""
            }`}
            onClick={() => handleTabClick("themes")}
          >
            Name Themes
          </div>
        </div>

        {activeTab === "styles" ? (
          <div className={classes.styleOptions}>
            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="friendly"
                  checked={selectedStyles.includes("friendly")}
                  onChange={() => handleStyleToggle("friendly")}
                />
                <label htmlFor="friendly">Friendly</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="tough"
                  checked={selectedStyles.includes("tough")}
                  onChange={() => handleStyleToggle("tough")}
                />
                <label htmlFor="tough">Tough</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="elegant"
                  checked={selectedStyles.includes("elegant")}
                  onChange={() => handleStyleToggle("elegant")}
                />
                <label htmlFor="elegant">Elegant</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="funny"
                  checked={selectedStyles.includes("funny")}
                  onChange={() => handleStyleToggle("funny")}
                />
                <label htmlFor="funny">Funny</label>
              </div>
            </div>

            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="sporty"
                  checked={selectedStyles.includes("sporty")}
                  onChange={() => handleStyleToggle("sporty")}
                />
                <label htmlFor="sporty">Sporty</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="cute"
                  checked={selectedStyles.includes("cute")}
                  onChange={() => handleStyleToggle("cute")}
                />
                <label htmlFor="cute">Cute</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="international"
                  checked={selectedStyles.includes("international")}
                  onChange={() => handleStyleToggle("international")}
                />
                <label htmlFor="international">International</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="classic"
                  checked={selectedStyles.includes("classic")}
                  onChange={() => handleStyleToggle("classic")}
                />
                <label htmlFor="classic">Classic</label>
              </div>
            </div>

            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="general"
                  checked={selectedStyles.includes("general")}
                  onChange={() => handleStyleToggle("general")}
                />
                <label htmlFor="general">General</label>
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.themeOptions}>
            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="movies"
                  checked={selectedThemes.includes("movies")}
                  onChange={() => handleThemeToggle("movies")}
                />
                <label htmlFor="movies">Movies</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="books"
                  checked={selectedThemes.includes("books")}
                  onChange={() => handleThemeToggle("books")}
                />
                <label htmlFor="books">Books</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="comics"
                  checked={selectedThemes.includes("comics")}
                  onChange={() => handleThemeToggle("comics")}
                />
                <label htmlFor="comics">Comics</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="geeky"
                  checked={selectedThemes.includes("geeky")}
                  onChange={() => handleThemeToggle("geeky")}
                />
                <label htmlFor="geeky">Geeky</label>
              </div>
            </div>

            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="music"
                  checked={selectedThemes.includes("music")}
                  onChange={() => handleThemeToggle("music")}
                />
                <label htmlFor="music">Music</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="historic"
                  checked={selectedThemes.includes("historic")}
                  onChange={() => handleThemeToggle("historic")}
                />
                <label htmlFor="historic">Historic</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="tv"
                  checked={selectedThemes.includes("tv")}
                  onChange={() => handleThemeToggle("tv")}
                />
                <label htmlFor="tv">TV</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="sports"
                  checked={selectedThemes.includes("sports")}
                  onChange={() => handleThemeToggle("sports")}
                />
                <label htmlFor="sports">Sports</label>
              </div>
            </div>

            <div className={classes.styleRow}>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="gaming"
                  checked={selectedThemes.includes("gaming")}
                  onChange={() => handleThemeToggle("gaming")}
                />
                <label htmlFor="gaming">Gaming</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="biblical"
                  checked={selectedThemes.includes("biblical")}
                  onChange={() => handleThemeToggle("biblical")}
                />
                <label htmlFor="biblical">Biblical</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="mythical"
                  checked={selectedThemes.includes("mythical")}
                  onChange={() => handleThemeToggle("mythical")}
                />
                <label htmlFor="mythical">Mythical</label>
              </div>
              <div className={classes.styleCheckbox}>
                <input
                  type="checkbox"
                  id="food"
                  checked={selectedThemes.includes("food")}
                  onChange={() => handleThemeToggle("food")}
                />
                <label htmlFor="food">Food</label>
              </div>
            </div>
          </div>
        )}

        <button
          className={`${classes.findNameButton} ${
            animating ? classes.animating : ""
          }`}
          onClick={generateName}
          disabled={animating}
        >
          <span className={classes.pawIcon}>🐾</span>
          {animating ? "Searching..." : "Find a Name"}
        </button>
      </div>

      {/* <div className={classes.favoriteCategories}>
        <h2>Favorite Categories</h2>

        <h3>Female cat Names</h3>
        <ul>
          <li>
            <a href="#">Cute cat Names</a>
          </li>
          <li>
            <a href="#">TV & Movie Cat Names</a>
          </li>
          <li>
            <a href="#">Unique Cat Names</a>
          </li>
        </ul>

        <h3>Male cat Names</h3>
        <ul>
          <li>
            <a href="#">Funny cat Names</a>
          </li>
          <li>
            <a href="#">Tough cat Names</a>
          </li>
          <li>
            <a href="#">Geeky Names</a>
          </li>
          <li>
            <a href="#">Nature cat Names</a>
          </li>
        </ul>
      </div> */}

      {/* Name Result Modal */}
      {showModal && (
        <div
          className={classes.modalOverlay}
          onClick={() => setShowModal(false)}
        >
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalContent}>
              <span
                className={classes.closeButton}
                onClick={() => setShowModal(false)}
              >
                ×
              </span>
              <h2>We found the perfect name!</h2>
              <div className={classes.nameResult}>
                <div className={classes.catIcon}>😻</div>
                <div className={classes.generatedName}>{generatedName}</div>
              </div>
              <p>
                How about this adorable name for your{" "}
                {gender === "boy" ? "boy" : "girl"} cat?
              </p>
              <div className={classes.modalButtons}>
                <button
                  className={classes.loveItButton}
                  onClick={() => setShowModal(false)}
                >
                  I Love It!
                </button>
                <button
                  className={classes.tryAgainButton}
                  onClick={() => {
                    setShowModal(false);
                    setTimeout(generateName, 300);
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RabbitNameGenerator;
